"use client";

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";

interface LayoutBounds {
  logoLeft: number;
  menuRight: number;
  headerHeight: number;
}

interface LayoutContextValue {
  bounds: LayoutBounds;
  updateBounds: (bounds: Partial<LayoutBounds>) => void;
  contentPadding: { left: number; right: number };
}

const defaultBounds: LayoutBounds = {
  logoLeft: 0,
  menuRight: 0,
  headerHeight: 0,
};

const LayoutContext = createContext<LayoutContextValue | null>(null);

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [bounds, setBounds] = useState<LayoutBounds>(defaultBounds);
  const [viewportWidth, setViewportWidth] = useState(0);

  const updateBounds = useCallback((newBounds: Partial<LayoutBounds>) => {
    setBounds((prev) => ({ ...prev, ...newBounds }));
  }, []);

  // Track viewport width for padding calculation
  useEffect(() => {
    const updateViewport = () => setViewportWidth(window.innerWidth);
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  // Calculate content padding based on header element positions
  const contentPadding = {
    left: bounds.logoLeft > 0 ? bounds.logoLeft : 16,
    right: bounds.menuRight > 0 ? viewportWidth - bounds.menuRight : 16,
  };

  return (
    <LayoutContext.Provider value={{ bounds, updateBounds, contentPadding }}>
      {children}
    </LayoutContext.Provider>
  );
}

export function useLayout() {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayout must be used within a LayoutProvider");
  }
  return context;
}

// Hook specifically for getting content padding
export function useContentPadding() {
  const { contentPadding } = useLayout();
  return contentPadding;
}
